import type { VersionedWording } from '@/lib/versioned-labels';

/**
 * How the versioned layer names a criterion, everywhere it has to.
 *
 * The layer is blind to content, so it is blind to the noun as well: every
 * sentence below is one it would otherwise have had to template by hand. They
 * are written out rather than built from « critère » because French articles
 * and agreements are not a concatenation - the day an object is feminine, a
 * template would read « le expérience » and nothing would catch it.
 *
 * `developmentHint` is the sentence the layer used to say for all four objects,
 * kept word for word by issue #155, which fixed the other three: it is the
 * maquette's, and it names the right referent. Its promise is the layer's
 * approximation all the same - a protocol cites a criterion at `production` by
 * default, and a session resolves it there (§5.2) - which is why the persona's
 * sentence stops before making one.
 *
 * The two sentences of the retirement are JSX, which is why this file is a
 * `.tsx` and holds no component: a name spliced into a template is ONE text node
 * where the prose around it is its own, and the two shapes land the tail of the
 * line a third of a pixel apart. Written as a fragment, the prose is what the
 * panel renders, character for character and node for node - which is what keeps
 * the capture of this screen identical to the one taken before this layer
 * existed.
 *
 * The two prefixes are what every `data-testid` of the screen is built from,
 * which is why they are spelled WITHOUT an accent: `critere-carte` is what
 * `criteres.spec.ts` and `screenshots-criteres.mjs` read, and a mark nobody can
 * type is a mark nobody can assert on.
 */
export const CRITERION_WORDING: VersionedWording = {
  kind: 'criterion',
  entity: 'critere',
  list: 'criteres',
  noun: ['critère', 'critères'],
  newTitle: 'Nouveau critère',
  createLabel: 'Créer un nouveau critère',
  developmentHint: (nextNumber) =>
    `Les liens en développement qui jouent ces protocoles verront la v${nextNumber} dès leur prochaine session.`,
  subject: (name) => `Critère « ${name} »`,
  retirement: {
    panelTitle: 'Retirer ce critère',
    retiredTitle: 'Critère retiré',
    confirmLabel: 'Retirer le critère',
    panelHint: (name) => (
      <>
        Il n’est plus proposé aux protocoles. Ceux qui le citent gardent un avertissement «
        remplacer le critère {name} » jusqu’au remplacement. Restauration possible depuis
        l’historique.
      </>
    ),
    retiredHint: (name, day) => (
      <>
        Retiré le {day}. Il n’est plus proposé aux protocoles, et les protocoles qui le citent
        portent l’avertissement « remplacer le critère {name} » jusqu’au remplacement. Les sessions
        passées gardent leur gel.
      </>
    ),
    warningTitle: (name) => `Chacun gardera « remplacer le critère ${name} »`,
  },
  refusal: {
    nameTaken: 'Ce nom est déjà porté par un autre critère. Choisissez-en un autre.',
    nameTakenBy: (name) =>
      `« ${name} » porte déjà ce nom. Choisissez-en un autre, ou renommez d’abord l’autre critère.`,
    retired: 'Ce critère est retiré. Restaurez-le avant de le modifier.',
  },
};
